import type React from 'react';
import type { ReactNode } from 'react';

// =============================================================================
// LAYER DESIGN TOKENS
// =============================================================================

/**
 * Layer animation duration constants in milliseconds.
 */
export const LAYER_ANIMATION_DURATIONS = {
  layer: 420,
  transition: 300,
} as const;

/**
 * Layer easing function presets.
 */
export const LAYER_EASING = {
  layerEnter: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  layerExit: 'cubic-bezier(0.4, 0.1, 0.8, 0.4)',
} as const;

/**
 * Layer dimension constants in pixels.
 */
export const LAYER_DIMENSIONS = {
  /** Visible peek height on mobile (10-12% of viewport, ~65px) */
  peekHeightMobile: 65,
  /** Visible peek height on tablet (15-18% of viewport, ~150px) */
  peekHeightTablet: 150,
  /** Scale transform for Layer 0 when Layer 1 is active (mobile) */
  peekScale: 0.96,
  /** Scale transform for Layer 0 when Layer 1 is active (tablet) */
  peekScaleTablet: 0.97,
  /** Brightness filter for Layer 0 when Layer 1 is active (mobile) */
  peekBrightness: 0.6,
  /** Brightness filter for Layer 0 when Layer 1 is active (tablet) */
  peekBrightnessTablet: 0.65,
  /** Distance threshold to trigger dismiss gesture (px) */
  dragThreshold: 80,
  /** Minimum drag distance with velocity threshold (px) */
  dragMinimum: 40,
  /** Velocity threshold for quick dismiss (px/s) */
  velocityThreshold: 500,
  /** Height of drag handle touch zone (px) */
  dragZoneHeight: 44,
  /** Layer 1 coverage on mobile (88-90%) */
  coverageMobile: 0.9,
  /** Layer 1 coverage on tablet (82-85%) */
  coverageTablet: 0.85,
} as const;

// =============================================================================
// LAYER LEVEL TYPES
// =============================================================================

/**
 * Layer level indicating depth in the layer stack.
 * - 0: Base layer (plan form)
 * - 1: Detail layer (workout/exercise detail)
 */
export type LayerLevel = 0 | 1;

// =============================================================================
// LAYER TRANSITION TYPES
// =============================================================================

/**
 * Transition animation types for layer content changes.
 * - 'slide-left': Current slides left, new enters from right (drill deeper)
 * - 'slide-right': Current slides right, previous enters from left (go back)
 * - 'crossfade': Crossfade between siblings (lateral navigation)
 * - 'none': Instant change without animation
 */
export type LayerTransition =
  'slide-left' | 'slide-right' | 'crossfade' | 'none';

// =============================================================================
// LAYER NAVIGATION TYPES
// =============================================================================

/**
 * Content item in the layer navigation stack.
 * Represents a single "page" within the layer system.
 */
export interface LayerNavigationItem {
  /** Unique identifier for this navigation item */
  id: string;
  /** Display label for breadcrumb */
  label: string;
  /** Type of content being displayed */
  type: 'plan' | 'workout' | 'exercise';
  /** Optional data associated with this item (workoutId, exerciseId, etc.) */
  data?: Record<string, unknown>;
}

/**
 * Breadcrumb item for layer navigation (uses onClick instead of href).
 */
export interface LayerBreadcrumbItem {
  /** Display label */
  label: string;
  /** Callback when item is tapped (triggers content transition, not page navigation) */
  onClick?: (() => void) | undefined;
  /** Whether this is the current/active item */
  isCurrent?: boolean;
  /** Optional icon to display before the label */
  icon?: ReactNode;
}

// =============================================================================
// LAYER STATE TYPES
// =============================================================================

/**
 * Complete layer system state.
 */
export interface LayerState {
  /** Whether Layer 1 is open */
  isOpen: boolean;
  /** Currently active layer (0 = base, 1 = detail) */
  activeLayer: LayerLevel;
  /** Navigation stack for Layer 1 content */
  navigationStack: LayerNavigationItem[];
  /** Current transition animation type */
  transition: LayerTransition;
}

/**
 * Initial/default layer state.
 */
export const INITIAL_LAYER_STATE: LayerState = {
  isOpen: false,
  activeLayer: 0,
  navigationStack: [],
  transition: 'none',
};

// =============================================================================
// LAYER SHEET PROPS
// =============================================================================

/**
 * LayerSheet component props.
 * Extends BottomSheet behavior with Layer 0 peek and content transitions.
 */
export interface LayerSheetProps {
  /** Whether Layer 1 is open */
  isOpen: boolean;
  /** Callback when layer should close */
  onClose: () => void;
  /** Current navigation stack for breadcrumb generation */
  navigationStack?: LayerNavigationItem[];
  /** Callback when breadcrumb item is tapped */
  onBreadcrumbNavigate?: (item: LayerNavigationItem, index: number) => void;
  /** Whether to show the drag handle */
  showDragHandle?: boolean;
  /** Custom class name */
  className?: string;
  /** Layer content */
  children: ReactNode;
  /** Footer content (pinned CTA) */
  footer?: ReactNode;
  /** ID for accessibility */
  id?: string;
}

// =============================================================================
// LAYER CONTENT PROPS
// =============================================================================

// =============================================================================
// LAYER BREADCRUMB PROPS
// =============================================================================

/**
 * LayerBreadcrumb component props.
 */
export interface LayerBreadcrumbProps {
  /** Breadcrumb items (derived from navigation stack) */
  items: LayerBreadcrumbItem[];
  /** Additional CSS class names */
  className?: string;
}

// =============================================================================
// LAYER CONTEXT TYPES
// =============================================================================

/**
 * Layer context value provided to consumers.
 */
export interface LayerContextValue {
  /** Current layer state */
  state: LayerState;
  /** Open Layer 1 with initial content */
  openLayer: (item: LayerNavigationItem) => void;
  /** Close Layer 1 and reset to Layer 0 */
  closeLayer: () => void;
  /** Navigate deeper within Layer 1 */
  pushContent: (item: LayerNavigationItem) => void;
  /** Navigate back within Layer 1 */
  popContent: () => void;
  /** Navigate to specific item in stack (for breadcrumb navigation) */
  navigateToIndex: (index: number) => void;
}

// =============================================================================
// LAYER HOOK RETURN TYPES
// =============================================================================

/**
 * Return type for useLayer hook.
 */
export interface UseLayerReturn {
  /** Whether Layer 1 is currently open */
  isOpen: boolean;
  /** Current active layer level */
  activeLayer: LayerLevel;
  /** Open Layer 1 with content */
  openLayer: (item: LayerNavigationItem) => void;
  /** Close Layer 1 */
  closeLayer: () => void;
  /** Toggle Layer 1 open/closed */
  toggleLayer: (item?: LayerNavigationItem) => void;
}

// =============================================================================
// LAYER CONTEXT INTERNAL TYPES (LayerContext.tsx)
// =============================================================================

export type LayerAction =
  | { type: 'OPEN_LAYER'; payload: LayerNavigationItem }
  | { type: 'CLOSE_LAYER' }
  | { type: 'PUSH_CONTENT'; payload: LayerNavigationItem }
  | { type: 'POP_CONTENT' }
  | { type: 'NAVIGATE_TO_INDEX'; payload: number }
  | { type: 'SET_TRANSITION'; payload: LayerTransition };

export interface LayerProviderProps {
  children: ReactNode;
}

// =============================================================================
// LAYER CONTENT INTERNAL TYPES (LayerContent.tsx)
// =============================================================================

export interface AnimationState {
  isAnimating: boolean;
  direction: LayerTransition;
  outgoingContent: ReactNode | null;
}

// =============================================================================
// LAYER SHEET INTERNAL TYPES (LayerSheet.tsx)
// =============================================================================

export interface LayerPeekZoneProps {
  onClick: () => void;
  peekHeight: number;
}

export interface DragHandleProps {
  onTouchStart: (e: React.TouchEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
}
